import {Heading, Text} from '@primer/react-brand/esm'
import {CheckIcon, ChevronRightIcon, HorizontalRuleIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'

import AccordionItem from './../../Accordion/AccordionItem/AccordionItem'
import useAccordionStates from './../../Accordion/hooks/useAccordionStates'
import type {FeaturesTableProps} from './../FeaturesTable.types'

import FeaturesTableStyles from './../FeaturesTable.module.css'
import Styles from './FeaturesTableDesktop.module.css'

export type FeaturesTableDesktopProps = FeaturesTableProps

const defaultProps: Partial<FeaturesTableDesktopProps> = {}

const FeaturesTableDesktop: React.FC<FeaturesTableDesktopProps> = props => {
  const initializedProps = {...defaultProps, ...props}
  const {title, columns, rows, aria} = initializedProps

  const {itemStates, updateItem} = useAccordionStates({items: rows})

  return (
    <table className={Styles['FeaturesTableDesktop']}>
      <thead className={clsx(Styles['FeaturesTableDesktop__row'], Styles['FeaturesTableDesktop__head'])}>
        <tr>
          <th scope="col" className={Styles['FeaturesTableDesktop__column--left']}>
            <Heading as="h3" size="5">
              {title}
            </Heading>
          </th>

          {columns.map(column => (
            <th scope="col" key={column.id}>
              <Text>{column.title}</Text>
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        {rows.map((row, index) => {
          const isExpanded = !!itemStates[index]

          const headingDOM = (
            <div
              className={clsx(
                Styles['FeaturesTableDesktop__featureItemTitleBlock'],
                isExpanded && Styles['FeaturesTableDesktop__featureItemTitleBlock--expanded'],
              )}
            >
              <ChevronRightIcon size={16} className={Styles['FeaturesTableDesktop__featureItemTitleBlock-chevron']} />
              <Text variant="muted">{row.title}</Text>
            </div>
          )

          const contentDOM = (
            <div
              className={clsx(
                Styles['FeaturesTableDesktop__featureItem-description'],
                Styles['FeaturesTableDesktop__column--left'],
              )}
            >
              {row.description}
            </div>
          )

          return (
            <tr key={row.id} className={Styles['FeaturesTableDesktop__row--featureItem']}>
              <td className={Styles['FeaturesTableDesktop__column--left']}>
                <AccordionItem
                  heading={headingDOM}
                  content={contentDOM}
                  isExpanded={isExpanded}
                  onToggle={state => updateItem(index, state)}
                  className={Styles['FeaturesTableDesktop__featureItem-accordion']}
                />
              </td>

              {columns.map((column, tierIndex) => {
                const content = row.content[tierIndex]
                if (!content) return null

                const {customText: availabilityText, availability: isAvailable} = content

                return (
                  <td
                    key={column.id}
                    className={clsx(
                      Styles['FeaturesTableDesktop__featureItemTier-availability'],
                      availabilityText
                        ? false
                        : isAvailable
                          ? Styles['FeaturesTableDesktop__featureItemTier-availability--green']
                          : Styles['FeaturesTableDesktop__featureItemTier-availability--grey'],
                    )}
                  >
                    {availabilityText ? (
                      availabilityText
                    ) : isAvailable ? (
                      <>
                        <CheckIcon size={24} />
                        <span className={FeaturesTableStyles['FeaturesTable__srOnly']}>{aria.included}</span>
                      </>
                    ) : (
                      <>
                        <HorizontalRuleIcon size={24} />
                        <span className={FeaturesTableStyles['FeaturesTable__srOnly']}>{aria.notIncluded}</span>
                      </>
                    )}
                  </td>
                )
              })}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

export default FeaturesTableDesktop
